import AsiListTableOptions from "@/components/common/AsiListTableOptions";
import PaginatedResponse from "@/models/PaginatedResponse";
import Vue from "vue";
import CreateResponse from "@/models/CreateResponse";
import UserListFilter from "@/models/user/UserListFilter";
import UserListEntry from "@/models/user/UserListEntry";
import IUserService from "@/services/IUserService";
import UserCreate from "@/models/user/UserCreate";
import User from "@/models/user/User";
import AvatarCreate from "@/models/attachment/AvatarCreate";
import {Permissions, Roles, UserState} from "@/helpers/constants";
import AddressUpdate from "@/models/address/AddressUpdate";
import UserUpdate from "@/models/user/UserUpdate";
import PlanningEntryListEntry from "@/models/planningEntry/PlanningEntryListEntry";
import ReportListFilter from "@/models/report/ReportListFilter";
import ReportListEntry from "@/models/report/ReportListEntry";
import PlanningEntryListFilter from "@/models/planningEntry/PlanningEntryListFilter";

export default class AxiosUserService implements IUserService {

	public user(id: string): Promise<User> {
		return Vue.$axios.get<User>(`/user/${id}`).then(res => res.data);
	}

	public users(filter: UserListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<UserListEntry>> {
		return Vue.$axios.get<PaginatedResponse<UserListEntry>>('/user', {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

	public create(model: UserCreate): Promise<CreateResponse> {
		return Vue.$axios.post<CreateResponse>(`/company/${model.companyId}/user`, model).then(res => res.data);
	}

	public update(id: string, model: UserUpdate): Promise<null> {
		return Vue.$axios.patch(`/user/${id}`, model).then(res => res.data);
	}

	public delete(id: string): Promise<null> {
		return Vue.$axios.delete(`/user/${id}`).then(res => res.data);
	}

	public state(id: string, requestedState: UserState): Promise<null> {
		return Vue.$axios.put(`/user/${id}/state`, {
			state: requestedState,
		}).then(res => res.data);
	}

	public uploadAvatar(model: AvatarCreate, onProgress: ((event: ProgressEvent) => void)): Promise<null> {
		const formData = new FormData();
		formData.append('avatar', model.file as Blob, model.file?.name);

		return Vue.$axios.put<null>(`/user/${model.model?.id}/avatar`, formData, {
			onUploadProgress: onProgress,
		}).then(res => res.data);
	}

	public deleteAvatar(model: User): Promise<null> {
		return Vue.$axios.delete(`/user/${model.id}/avatar`).then(res => res.data);
	}

	public updateAddress(id: string, model: AddressUpdate): Promise<null> {
		return Vue.$axios.patch(`/user/${id}/address`, model).then(res => res.data);
	}

	public updateUsername(id: string, username: string): Promise<null> {
		return Vue.$axios.put(`/user/${id}/username`, {
			username: username,
		}).then(res => res.data);
	}

	public updatePassword(id: string, password: string): Promise<null> {
		return Vue.$axios.put(`/user/${id}/password`, {
			password: password,
		}).then(res => res.data);
	}

	public roles(id: string): Promise<Roles[]> {
		return Vue.$axios.get(`/auth/${id}/role`).then(res => res.data);
	}

	public assignRole(id: string, role: Roles): Promise<null> {
		return Vue.$axios.post(`/auth/${id}/role`, {
			roleName: role,
		}).then(res => res.data);
	}

	public revokeRole(id: string, role: Roles): Promise<null> {
		return Vue.$axios.delete(`/auth/${id}/role/${role}`).then(res => res.data);
	}

	public permissions(id: string): Promise<Permissions[]> {
		return Vue.$axios.get(`/auth/${id}/permission`).then(res => res.data);
	}

	public assignPermission(id: string, permission: Permissions): Promise<null> {
		return Vue.$axios.post(`/auth/${id}/permission`, {
			permissionName: permission,
		}).then(res => res.data);
	}

	public revokePermission(id: string, permission: Permissions): Promise<null> {
		return Vue.$axios.delete(`/auth/${id}/permission/${permission}`).then(res => res.data);
	}

	public planningEntries(id: string, filter: PlanningEntryListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<PlanningEntryListEntry>> {
		return Vue.$axios.get<PaginatedResponse<PlanningEntryListEntry>>(`/user/${id}/planning-entry`, {
			params: {
				...filter,
				...options
			}
		}).then(res => res.data);
	}

	public reports(id: string, filter: ReportListFilter | null, options: AsiListTableOptions | null): Promise<PaginatedResponse<ReportListEntry>> {
		return Vue.$axios.get<PaginatedResponse<ReportListEntry>>(`/user/${id}/report`, {
			params: {
				...filter,
				...options
			}
		})
			.then(res => res.data);
	}

}
